import { Alert } from "@svea/design-system-components-react";
import {
	Checkbox,
	type FieldOption,
	Fieldset,
	FormControlLabel,
	FormHelperText,
	Stack,
	useText,
} from "@sveabank/cc-common";
import { useState } from "react";
import { Field, type FieldInputProps } from "react-final-form";
import { usePageContext } from "../../../../hooks/context";
import type {
	ClcRepresentative,
	ClcShareholder,
	RoleValue,
} from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";
import ClcPersonCard from "../shared/person-card";
import { DeleteRepresentativeModal } from "./delete-representative-modal";

type Props = {
	name: string;
	representative: ClcRepresentative;
	shareholders: ClcShareholder[];
	onRemovePerson: () => void;
};

const getIsChecked = (
	{ value }: FieldInputProps<Array<string>, HTMLElement>,
	option: FieldOption,
) => !!value && value.some((v) => v === option.value);

const getIsDisabled = (currentRoles: Array<RoleValue>, role: RoleValue, isApplicant: boolean) => {
	switch (role) {
		case "BoardMember":
			return (
				isApplicant ||
				currentRoles.includes("AlternateBoardMember") ||
				currentRoles.includes("ExternalAuthorisedSignatory")
			);
		case "AlternateBoardMember":
			return (
				currentRoles.includes("BoardMember") || currentRoles.includes("ExternalAuthorisedSignatory")
			);
		case "Chairman":
			return currentRoles.includes("ExternalAuthorisedSignatory");
		case "ExternalAuthorisedSignatory":
			return (
				currentRoles.includes("AlternateBoardMember") ||
				currentRoles.includes("BoardMember") ||
				currentRoles.includes("Chairman")
			);
		default:
			return false;
	}
};

const onCheckboxChange = (
	{ onChange, value: values = [] }: FieldInputProps<Array<string>, HTMLElement>,
	checked: boolean,
	option: FieldOption,
) => {
	const newValues = checked ? [option.value, ...values] : values.filter((v) => v !== option.value);
	onChange(newValues);
};

const RepresentativeField = ({ name, representative, shareholders, onRemovePerson }: Props) => {
	const {
		roles,
		clcApplicationDetails: { applicant },
	} = usePageContext(useClcCompanyApplication);

	const [t, texts] = useText();
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const isApplicant = representative.nationalId === applicant.nationalId;
	const isShareholder = !!shareholders.find((sh) => sh.nationalId === representative.nationalId);

	const currentRoles = representative.functions || [];

	return (
		<>
			<ClcPersonCard
				person={representative}
				onDelete={isApplicant || isShareholder ? undefined : () => setIsDeleteModalOpen(true)}
				isApplicant={isApplicant}
				isShareholder={isShareholder}
			>
				{isApplicant && (
					<Alert kind="information">{t(texts.newCompany.companyStructure.applicantInfo)}</Alert>
				)}
				<Field<Array<string>>
					name={`${name}.functions`}
					format={(v) => v || []}
					validate={(v) => (!v.length ? t(texts.validation.chooseOne) : undefined)}
				>
					{({ input, meta }) => (
						<Fieldset>
							<Stack gap={2}>
								{roles.map((role) => {
									const isDisabled = getIsDisabled(currentRoles, role.value, isApplicant);
									const isChecked = getIsChecked(input, role);

									return (
										<FormControlLabel label={role.label} key={role.value} disabled={isDisabled}>
											<Checkbox
												name={input.name}
												disabled={isDisabled}
												value={role.value}
												onChange={(e) => {
													onCheckboxChange(input, !!e.target.checked, role);
												}}
												checked={isChecked}
											/>
										</FormControlLabel>
									);
								})}
								{meta.error && meta.touched && <FormHelperText error>{meta.error}</FormHelperText>}
							</Stack>
						</Fieldset>
					)}
				</Field>
			</ClcPersonCard>
			<DeleteRepresentativeModal
				open={isDeleteModalOpen}
				setOpen={setIsDeleteModalOpen}
				person={representative}
				deleteRepresentative={onRemovePerson}
			/>
		</>
	);
};

export default RepresentativeField;
